import { useRef } from 'react'
import { useFormikContext } from 'formik'
import get from 'lodash/get'
import { AnySchema } from 'yup'

type SaveValue = string | number | null

const isBlank = (value: unknown) =>
  value === null || value === undefined || value === ''

// a formatted number like "12,500" is the same as a saved 12500
const isSameValue = (a: unknown, b: unknown) => {
  if (isBlank(a) || isBlank(b)) return isBlank(a) && isBlank(b)
  if (String(a) === String(b)) return true

  const numberA = Number(String(a).replace(/,/g, ''))
  const numberB = Number(String(b).replace(/,/g, ''))
  return Number.isFinite(numberA) && numberA === numberB
}

// Only calls `save` when the new value passes `schema` for this field and
// differs from the value it last saved (or started with), or from
// `savedValues` when the record can change elsewhere.
const useValidatedSave = ({
  schema,
  save,
  savedValues,
}: {
  schema: AnySchema
  save: ({ name, value }: { name: string; value: SaveValue }) => void
  savedValues?: Record<string, unknown>
}) => {
  const { values, initialValues } = useFormikContext<Record<string, unknown>>()
  const lastSaved = useRef<Record<string, unknown>>({})

  return async ({ name, value }: { name: string; value: SaveValue }) => {
    // a cleared input is '', which number fields can't accept
    const savedValue = value === '' ? null : value

    const previousValue = savedValues
      ? get(savedValues, name)
      : name in lastSaved.current
        ? lastSaved.current[name]
        : get(initialValues, name)
    if (isSameValue(previousValue, savedValue)) return

    try {
      await schema.validateAt(name, { ...values, [name]: savedValue })
    } catch {
      // invalid per the schema — do not save
      return
    }
    lastSaved.current[name] = savedValue
    // a save's own hook shows its errors, so a failed one is left there
    // rather than going unhandled
    Promise.resolve(save({ name, value: savedValue })).catch(() => undefined)
  }
}

export default useValidatedSave
